/*
 * The CustomerConfirmSubscriptionStep component is the step in customer SO authorization flow where subscriptions are
 * viewed and confirmed.
 * 
 * This takes the following props:
 *   auth                 - authentication object
 *   account              - customer for which this SO is being created
 *   project              - project for which this SO is being created
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Switch, Typography,
         TableContainer, Table, TableBody, TableCell,
         TableHead, TableRow, TableSortLabel } from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import AppBindStyles from '../../AppBindStyles';

import SubscriptionTableRow from '../suborder/SubscriptionTableRow';

const styleSheet = theme => ({
  paper: {
    padding: theme.spacing(3),
    textAlign: 'left',
    // maxWidth: 980
  },
  subCell: {
    minWidth: 192
  },
  root: {
    flexGrow: 1,
  },
  accountLogo: {
    float: 'left',
    marginRight: 15
  },
  button: {
    width: 52,
  },
  input: {
    display: 'none',
  },
  newAppRow: {
    // backgroundColor: yellow[100],
    borderLeft: 'solid',
    borderLeftWidth: 3,
    borderLeftColor: '#81C784;'
  },
  termsChangedRow: {
    borderLeft: 'dotted',
    borderLeftWidth: 3,
    borderLeftColor: '#FFBF00;'
  },
  existingAppRow: {
    marginLeft: 6
  },
  appSpacer: {
    marginLeft: 3
  },
  terms: {
    maxWidth: 170,
    minWidth: 170
  }
});

const StyledTable = styled(Table) (({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  //borderColor: theme.palette.colors.secondary[200],
  borderColor: theme.palette.divider,
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
    // backgroundColor: theme.palette.colors.secondary[200],
    backgroundColor: theme.palette.divider,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  /*
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  */
  height: 50, // Make the row heights consistent.  Smaller than this could be better, but then padding needs to be adjusted.
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

class CustomerConfirmSubscriptionStep extends React.Component {
  
  state = {
    sortBy:     'appname',
    sortOrder:  'asc',
    filterNew:  1,
  };
  
  isBackDisabled() {
    if (this.props.activeStep === 0)
      return true;

    return false;
  }

  isNextDisabled() {
    if (this.props.activeStep === this.props.assignedStep) {
      return false;
    }
    return false;
  }

  getValueForRender(state_member, prop_member, theDefault)
  {
    if (state_member != null)
      return state_member;

    if (prop_member != null)
      return prop_member;

    return theDefault;
  }

  toggleOrder = (ob) => {
    if (ob === this.state.sortBy) {
      if (this.state.sortOrder === 'desc') {
        this.setState({sortOrder: 'asc'});
      } else {
        this.setState({sortOrder: 'desc'});
      }
    } else {
      this.setState({sortBy: ob, sortOrder: 'asc'});
    }
  } 

  render() {
    const classes = this.props.classes;

    let displayOnly = false;
    if (this.props.activeStep > this.props.assignedStep) {
      displayOnly = true;
    }

    let theAccount = this.props.account;
    let theProject = this.props.project;

    let rPreApproveSubs = theAccount.rPreApproveSubs
    if (theProject != null) {
      rPreApproveSubs = theProject.rPreApproveSubs;
    }

    let allSubscriptions = [];
    let filteredSubscriptions = [];
    let unchangedCount = 0; // only relevant when not displayOnly
    let newOrChangedCount = 0;   // only relevant when not displayOnly

    // Pre-pass to remove deleted subscriptions
    for (let i = 0; i < theAccount.customerAppTsByCustomerAccountId.nodes.length; i++) {
      let sub = theAccount.customerAppTsByCustomerAccountId.nodes[i];
      if (sub.appStatus === 2) {
        continue;
      }
      allSubscriptions.push(sub);
    }

    // Do the sort on this set of subscriptions
    // - allow sort by model or subscription name
    //
    if (allSubscriptions.length > 1) {
      if (this.state.sortBy === 'model') {
        // Sorting by model means paymentOrder
        // But we're going to sort alpha by the decoded names, so "Ask customer for access" (4), then "Expense to customer" (0 or 1), then "I will pay" (2) 
        if (this.state.sortOrder === 'desc') {
          allSubscriptions.sort(function(a, b) {
            /* Sort by model, then app name alpha */
            let aModel = a.paymentOrder;
            if (a.paymentOrder === 4 || a.bindCardTByBcId.bcStatus === 4) {
              aModel = 1;
            } else if (a.paymentOrder === 2) {
              aModel = 2;
              } else if (a.paymentOrder === 0 || a.paymentOrder === 1) {
              aModel = 3;
            }

            let bModel = b.paymentOrder;
            if (b.paymentOrder === 4 || b.bindCardTByBcId.bcStatus === 4) {
              bModel = 1;
            } else if (b.paymentOrder === 2) {
              bModel = 2;
              } else if (b.paymentOrder === 0 || b.paymentOrder === 1) {
              bModel = 3;
            }

            if (aModel < bModel) { return 1; }
            if (aModel > bModel) { return -1; }

            if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
            if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
            return 0;
          });
        } else {
          allSubscriptions.sort(function(a, b) {
            /* Sort by model, then app name alpha */
            // Treat 0 and 1 as the same
            let aModel = a.paymentOrder;
            if (a.paymentOrder === 4 || a.bindCardTByBcId.bcStatus === 4) {
              aModel = 1;
            } else if (a.paymentOrder === 2) {
              aModel = 2;
              } else if (a.paymentOrder === 0 || a.paymentOrder === 1) {
              aModel = 3;
            }

            let bModel = b.paymentOrder;
            if (b.paymentOrder === 4 || b.bindCardTByBcId.bcStatus === 4) {
              bModel = 1;
            } else if (b.paymentOrder === 2) {
              bModel = 2;
              } else if (b.paymentOrder === 0 || b.paymentOrder === 1) {
              bModel = 3;
            }

            if (aModel < bModel) { return -1; }
            if (aModel > bModel) { return 1; }
            
            if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
            if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
            return 0;
          });
        }
      } else {
        // Sort by app name
        if (this.state.sortOrder === 'desc') {
          allSubscriptions.sort(function(a, b) {
            if(a.appTByAppId.appName < b.appTByAppId.appName) { return 1; }
            if(a.appTByAppId.appName > b.appTByAppId.appName) { return -1; }
            return 0;
          });
        } else {
          allSubscriptions.sort(function(a, b) {
            if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
            if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
            return 0;
          });
        }
      }
    }

    // Now further categorize
    for (let i = 0; i < allSubscriptions.length; i++) {
      let sub = allSubscriptions[i];

      let thisSubscriptionNewOrChanged = true;
      if (sub.paymentOrder === 0 || sub.paymentOrder === 1 || sub.paymentOrder === 2) {
        if (sub.bindCardTByBcId.bcStatus === -1) {
          newOrChangedCount++;
        } else {
          if (sub.rPassThroughFees !== null || sub.rAppMarkupPercentage !== null || sub.rPaymentOrder != null) {
            newOrChangedCount++;
          } else {
            unchangedCount++;
            thisSubscriptionNewOrChanged = false;
          }  
        }
      } else if (sub.paymentOrder === 4 || sub.bindCardTByBcId.bcStatus === 4) {
        if (sub.pwd == null) {
          newOrChangedCount++;
        } else {
          unchangedCount++;
          thisSubscriptionNewOrChanged = false;
        }
      }

      if (this.state.filterNew === 1 && thisSubscriptionNewOrChanged === false) {
        continue;
      }

      filteredSubscriptions.push(sub);
    }

    // Show the filter switch if:
    // - not display only
    // - there are both new/changed and unchanged subscriptions
    let showFilterSwitch = false;
    if (newOrChangedCount > 0 && unchangedCount > 0) {
      showFilterSwitch = true;
    }

    // Edge case - if there are no new or changed subscriptions, then the subscriptionsToDisplay array will be empty.
    // In this case, we should show the existing subscriptions, and there is no filter switch as the filter would
    // show nothing.
    //
    let subscriptionsToDisplay = [];
    if (filteredSubscriptions.length > 0) {
      for (let i=0; i<filteredSubscriptions.length; i++) {
        let sub = filteredSubscriptions[i];
        subscriptionsToDisplay.push(sub);
      }
    } else {
      for (let i=0; i<allSubscriptions.length; i++) {
        let sub = allSubscriptions[i];
        subscriptionsToDisplay.push(sub);
      }
    }

    // Show sorting if:
    // - not display only
    // - there are more than 1 subscriptions
    let showSorting = false;
    if (subscriptionsToDisplay.length > 1) {
      showSorting = true;
    }

    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Grid container spacing={0} direction="row" justifyContent="space-between" >
            <Grid item xs={12}>
              <Typography variant="h6">
                Subscriptions for your project
              </Typography>
              </Grid>
            <Grid item xs={12}>
                <Typography variant="body2">We need your authorization to manage these subscriptions for your project.</Typography>
            </Grid>
          </Grid>
        </Grid>

        {showFilterSwitch === true &&
        <Grid item xs={12}>
          <Grid container spacing={1} justifyContent="flex-start" alignItems="center">
            <Grid item>
              <Typography variant="body1">Only show new and changed subscriptions</Typography>
            </Grid>
            <Grid item>
              <Switch checked={this.state.filterNew === 1}
                      onChange={(e, checked) => {
                        if (checked) {
                          this.setState({ filterNew: 1});
                        } else {
                          this.setState({ filterNew: 0});
                        }
                      }}
              />
            </Grid>
          </Grid>
        </Grid>} 

        {subscriptionsToDisplay.length > 0 && 
        <Grid item xs={12}>
          <TableContainer>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ borderTopLeftRadius: 8}}/>
                  <StyledTableCell className={classes.subCell}>
                    {showSorting === true &&
                    <TableSortLabel
                      active={this.state.sortBy === 'appname'}
                      direction={this.state.sortOrder}
                      onClick={() => this.toggleOrder('appname') }>
                      Subscription
                    </TableSortLabel>}
                    {showSorting === false &&
                    <React.Fragment>Subscription</React.Fragment>}
                  </StyledTableCell>
                  <StyledTableCell>
                    {showSorting === true &&
                    <TableSortLabel
                      active={this.state.sortBy === 'model'}
                      direction={this.state.sortOrder}
                      onClick={() => this.toggleOrder('model') }>
                      Sharing model
                    </TableSortLabel>}
                    {showSorting === false &&
                    <React.Fragment>Model</React.Fragment>}
                  </StyledTableCell>
                  <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptionsToDisplay.map(n => {
                  return (
                    <SubscriptionTableRow 
                      key={n.customerAppId}
                      subscription={n}
                      styledTableRow={StyledTableRow}
                      styledTableCell={StyledTableCell}
                      editable={false}
                      showModel={false}
                      soContext="customerView"
                    />
                  );
                })}
              </TableBody>
            </StyledTable>
          </TableContainer>                  
        </Grid>}

        {rPreApproveSubs === 1 &&
        <Grid item xs={12}>
          <Typography style={AppBindStyles.boldLabel}>Additional subscriptions as needed.</Typography>
        </Grid>}

        {displayOnly === false && this.props.activeStep === this.props.assignedStep &&
        <Grid item xs={12}>
          { !this.isBackDisabled() &&
          <Button
            disabled={this.isBackDisabled()}
            color="primary" variant="outlined"
            onClick={() => this.props.handleBack()}
            style={AppBindStyles.bottomButton}>
            Back
          </Button>}

          <Button
            color="primary" variant="contained"
            disabled={this.isNextDisabled()}
            onClick={() => this.props.handleNext()}
            style={AppBindStyles.bottomButton}>
              Confirm
          </Button>
        </Grid>} 
      </Grid>       
    );
  }
}

CustomerConfirmSubscriptionStep.propTypes = {
  assignedStep: PropTypes.number.isRequired,
  activeStep: PropTypes.number.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default withStyles(styleSheet)(CustomerConfirmSubscriptionStep);
